import { brandVisualUniverseSchema } from "@runcheetah/shared/brand-profile";

import type { BrandVisualUniverse } from "./brand-visual-universe.ts";

const cleanText = (value: string): string => value.trim();

const cleanOptionalText = (value: string | undefined): string | undefined => {
  const cleaned = value?.trim();
  return cleaned ? cleaned : undefined;
};

const cleanTextList = (items: readonly string[]): string[] =>
  items.map(cleanText).filter((item) => item.length > 0);

const areTextListsEqual = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areObjectListsEqual = <T>(
  left: readonly T[],
  right: readonly T[],
  isEqual: (leftItem: T, rightItem: T) => boolean,
): boolean =>
  left.length === right.length && left.every((leftItem, index) => isEqual(leftItem, right[index]!));

export const createBrandVisualUniverseMotifDraft = (): BrandVisualUniverse["motifs"][number] => ({
  name: "",
  meaning: "",
  usage: "",
  kind: "content",
});

export const cloneBrandVisualUniverseDraft = (
  visualUniverse: BrandVisualUniverse,
): BrandVisualUniverse => ({
  summary: visualUniverse.summary,
  sourceLogic: [...visualUniverse.sourceLogic],
  photographyArchetypes: visualUniverse.photographyArchetypes.map((item) => ({
    ...item,
    subjects: [...item.subjects],
    settings: [...item.settings],
  })),
  environments: visualUniverse.environments.map((item) => ({ ...item, cues: [...item.cues] })),
  materials: visualUniverse.materials.map((item) => ({ ...item })),
  motifs: visualUniverse.motifs.map((item) => ({ ...item })),
  colorRoles: visualUniverse.colorRoles.map((item) => ({ ...item, colors: [...item.colors] })),
  compositionRules: [...visualUniverse.compositionRules],
  promptPatterns: visualUniverse.promptPatterns.map((item) => ({ ...item })),
  antiPatterns: [...visualUniverse.antiPatterns],
});

export const normalizeBrandVisualUniverseDraft = (
  draft: BrandVisualUniverse,
): BrandVisualUniverse =>
  brandVisualUniverseSchema.parse({
    summary: cleanText(draft.summary),
    sourceLogic: cleanTextList(draft.sourceLogic),
    photographyArchetypes: draft.photographyArchetypes.map((item) => ({
      name: cleanText(item.name),
      description: cleanText(item.description),
      subjects: cleanTextList(item.subjects),
      settings: cleanTextList(item.settings),
      lighting: cleanText(item.lighting),
      framing: cleanOptionalText(item.framing),
      useWhen: cleanText(item.useWhen),
    })),
    environments: draft.environments.map((item) => ({
      name: cleanText(item.name),
      description: cleanText(item.description),
      cues: cleanTextList(item.cues),
    })),
    materials: draft.materials.map((item) => ({
      name: cleanText(item.name),
      usage: cleanText(item.usage),
      sensoryCue: cleanText(item.sensoryCue),
    })),
    motifs: draft.motifs.map((item) => ({
      name: cleanText(item.name),
      meaning: cleanText(item.meaning),
      usage: cleanText(item.usage),
      kind: item.kind,
    })),
    colorRoles: draft.colorRoles.map((item) => ({
      name: cleanText(item.name),
      colors: cleanTextList(item.colors),
      role: cleanText(item.role),
      usageGuidance: cleanText(item.usageGuidance),
    })),
    compositionRules: cleanTextList(draft.compositionRules),
    promptPatterns: draft.promptPatterns.map((item) => ({
      name: cleanText(item.name),
      pattern: cleanText(item.pattern),
      example: cleanText(item.example),
      useWhen: cleanText(item.useWhen),
    })),
    antiPatterns: cleanTextList(draft.antiPatterns),
  });

export const areBrandVisualUniverseDraftsEqual = (
  left: BrandVisualUniverse,
  right: BrandVisualUniverse,
): boolean =>
  left.summary === right.summary &&
  areTextListsEqual(left.sourceLogic, right.sourceLogic) &&
  areObjectListsEqual(
    left.photographyArchetypes,
    right.photographyArchetypes,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      leftItem.description === rightItem.description &&
      areTextListsEqual(leftItem.subjects, rightItem.subjects) &&
      areTextListsEqual(leftItem.settings, rightItem.settings) &&
      leftItem.lighting === rightItem.lighting &&
      leftItem.framing === rightItem.framing &&
      leftItem.useWhen === rightItem.useWhen,
  ) &&
  areObjectListsEqual(
    left.environments,
    right.environments,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      leftItem.description === rightItem.description &&
      areTextListsEqual(leftItem.cues, rightItem.cues),
  ) &&
  areObjectListsEqual(
    left.materials,
    right.materials,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      leftItem.usage === rightItem.usage &&
      leftItem.sensoryCue === rightItem.sensoryCue,
  ) &&
  areObjectListsEqual(
    left.motifs,
    right.motifs,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      leftItem.meaning === rightItem.meaning &&
      leftItem.usage === rightItem.usage &&
      leftItem.kind === rightItem.kind,
  ) &&
  areObjectListsEqual(
    left.colorRoles,
    right.colorRoles,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      areTextListsEqual(leftItem.colors, rightItem.colors) &&
      leftItem.role === rightItem.role &&
      leftItem.usageGuidance === rightItem.usageGuidance,
  ) &&
  areTextListsEqual(left.compositionRules, right.compositionRules) &&
  areObjectListsEqual(
    left.promptPatterns,
    right.promptPatterns,
    (leftItem, rightItem) =>
      leftItem.name === rightItem.name &&
      leftItem.pattern === rightItem.pattern &&
      leftItem.example === rightItem.example &&
      leftItem.useWhen === rightItem.useWhen,
  ) &&
  areTextListsEqual(left.antiPatterns, right.antiPatterns);
